import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild } from '@angular/core';
import { CustomIcons } from '@shared/models/custom-icons';
import { SelectModelImpaired } from '@impairedShared/select-impaired/models';
import { MatPaginator } from '@angular/material/paginator';

@Component({
  selector: 'app-impaired-pagination',
  templateUrl: './impaired-pagination.component.html',
  styleUrls: ['./impaired-pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImpairedPaginationComponent {
  private _defaultPageSizeList: SelectModelImpaired[] = [
    {
      value: 10,
      title: 10,
      titleEn: 10,
      titleRu: 10
    },
    {
      value: 20,
      title: 20,
      titleEn: 20,
      titleRu: 20
    },
    {
      value: 50,
      title: 50,
      titleEn: 50,
      titleRu: 50
    }
  ];
  @Input() public pageSize = 10;
  @ViewChild(MatPaginator, { static: true }) public paginator: MatPaginator;
  @Input() public pageSizeOption: SelectModelImpaired = {
    value: 10,
    title: 10,
    titleEn: 10,
    titleRu: 10
  };
  @Input() public allCount = 0;
  @Input() public currentCount = 0;

  @Input() public set hasShowAll(hasShowAll: boolean) {
    if (hasShowAll) {
      this.pageSizeList = [
        ...this._defaultPageSizeList,
        {
          value: -1,
          title: 'Все',
          titleEn: 'All',
          titleRu: 'Все'
        }
      ];
    } else {
      this.pageSizeList = [
        ...this._defaultPageSizeList,
        {
          value: 100,
          title: 100,
          titleEn: 100,
          titleRu: 100
        }
      ];
    }
  }
  @Output() public readonly paginatorChanged: EventEmitter<{
    pageIndex: number;
    pageSize: number;
  }> = new EventEmitter();
  public hasNextPage = true;
  public hasLastPage = true;
  public hasPrevPage = false;
  public hasPrevFirstPage = false;
  public icons = CustomIcons;
  public pageIndex: number | null = 1;
  public prevPageIndex: number | null;
  public pageSizeList: SelectModelImpaired[] = [
    ...this._defaultPageSizeList,
    {
      value: 100,
      title: 100,
      titleEn: 100,
      titleRu: 100
    }
  ];

  constructor(public cdr: ChangeDetectorRef) {}
  private paginatorChange(): void {
    if (this.pageIndex) {
      this.paginatorChanged.emit({
        pageSize: this.pageSize,
        pageIndex: this.pageIndex
      });
    }
  }

  public pageIndexChange(hasPaginatorChange = true): void {
    if (this.pageIndex === null) {
      return;
    }

    let lastPage = this.currentCount / this.pageSize;
    if (Math.trunc(lastPage) < lastPage) {
      lastPage = Math.trunc(lastPage) + 1;
    } else if (lastPage === 0) {
      lastPage = 1;
    }
    if (this.pageIndex === 0 || this.pageIndex < 0) {
      this.pageIndex = 1;
      this.hasNextPage = true;
      this.hasLastPage = true;
      this.hasPrevPage = false;
      this.hasPrevFirstPage = false;
    } else if (this.pageIndex >= lastPage) {
      this.pageIndex = lastPage;
      if (lastPage === 1) {
        this.hasPrevPage = false;
        this.hasPrevFirstPage = false;
      } else {
        this.hasPrevPage = true;
        this.hasPrevFirstPage = true;
      }
      this.hasNextPage = false;
      this.hasLastPage = false;
    } else if (this.pageIndex === 1) {
      this.hasNextPage = true;
      this.hasLastPage = true;
      this.hasPrevPage = false;
      this.hasPrevFirstPage = false;
    } else {
      this.hasNextPage = true;
      this.hasLastPage = true;
      this.hasPrevPage = true;
      this.hasPrevFirstPage = true;
    }

    if (hasPaginatorChange) {
      this.paginatorChange();
    }
  }

  public clearInput(): void {
    this.prevPageIndex = this.pageIndex;
    this.pageIndex = null;
  }
  public setInput(): void {
    if (!this.pageIndex) {
      this.pageIndex = this.prevPageIndex;
    }
    this.pageIndexChange(true);
  }

  public numberOnly($event: KeyboardEvent): boolean {
    const charCode = $event.which ? $event.which : $event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  public pageSizeChangedSelect(newPageSize: SelectModelImpaired): void {
    if (newPageSize.value) {
      const pageSize = +newPageSize.value;
      if (!isNaN(pageSize)) {
        this.pageSizeOption = newPageSize;
        this.pageSizeChanged(pageSize);
      }
    }
  }

  public pageSizeChanged(newPageSize: number): void {
    if (newPageSize === -1) {
      this.pageIndex = 1;
      this.hasNextPage = false;
      this.hasLastPage = false;
      this.hasPrevPage = false;
      this.hasPrevFirstPage = false;
      this.pageSize = newPageSize;
      this.paginatorChange();
      return;
    }

    const pageSize = newPageSize;

    const newMaxPageCountFract = this.currentCount / pageSize;

    const newMaxPageCount =
      newMaxPageCountFract - Math.trunc(newMaxPageCountFract) === 0 ? newMaxPageCountFract : Math.trunc(newMaxPageCountFract) + 1;

    if (this.pageIndex) {
      if (Math.trunc(newMaxPageCount) === 0 || Math.trunc(newMaxPageCountFract) === 0) {
        this.pageIndex = 1;
        this.prevPageIndex = 1;
      } else if (newMaxPageCount < this.pageIndex) {
        this.pageIndex = newMaxPageCount;
        this.prevPageIndex = newMaxPageCount;
      }
    }

    this.hasNextPage = true;
    this.hasLastPage = true;
    this.hasPrevPage = true;
    this.hasPrevFirstPage = true;

    if (this.pageIndex === 1) {
      this.hasNextPage = true;
      this.hasLastPage = true;
      this.hasPrevPage = false;
      this.hasPrevFirstPage = false;
    }
    if (this.pageIndex === newMaxPageCount) {
      this.hasNextPage = false;
      this.hasLastPage = false;
      this.hasPrevPage = true;
      this.hasPrevFirstPage = true;
    }

    if (newMaxPageCount === 1) {
      this.hasNextPage = false;
      this.hasLastPage = false;
      this.hasPrevPage = false;
      this.hasPrevFirstPage = false;
    }

    this.pageSize = newPageSize;
    this.paginatorChange();
  }

  public nextPage(pageSizeIncrement: number | null): void {
    const maxPageCountFract = this.currentCount / this.pageSize;
    const maxPageCount = maxPageCountFract - Math.trunc(maxPageCountFract) === 0 ? maxPageCountFract : Math.trunc(maxPageCountFract) + 1;

    if (!pageSizeIncrement) {
      this.pageIndex = maxPageCount;
      this.hasNextPage = false;
      this.hasLastPage = false;
      this.hasPrevFirstPage = true;
      this.hasPrevPage = true;
      this.paginatorChange();
      return;
    }
    if (!this.pageIndex) {
      return;
    }

    const newPageSize = pageSizeIncrement + this.pageIndex;
    if (maxPageCount > newPageSize) {
      this.pageIndex = newPageSize;
    } else if (maxPageCount === newPageSize) {
      this.pageIndex = newPageSize;
      this.hasNextPage = false;
      this.hasLastPage = false;
    }

    this.hasPrevFirstPage = true;
    this.hasPrevPage = true;

    this.paginatorChange();
  }

  public prevPage(pageSizeDecrement: number | null): void {
    if (!pageSizeDecrement) {
      this.pageIndex = 1;
      this.hasNextPage = true;
      this.hasLastPage = true;
      this.hasPrevFirstPage = false;
      this.hasPrevPage = false;
      this.paginatorChange();
      return;
    }
    if (!this.pageIndex) {
      return;
    }

    const newPageSize = this.pageIndex - pageSizeDecrement;
    if (newPageSize > 1) {
      this.pageIndex = newPageSize;
    } else if (newPageSize === 1) {
      this.pageIndex = newPageSize;
      this.hasPrevFirstPage = false;
      this.hasPrevPage = false;
    }

    this.hasNextPage = true;
    this.hasLastPage = true;

    this.paginatorChange();
  }

  public updatePaginator(): void {
    this.cdr.detectChanges();
  }
}
